import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, FileText, Newspaper, Search } from 'lucide-react';
import {
  fetchAdminSeo,
  fetchAdminSeoEntry,
  patchAdminSeoEntry,
  patchAdminSiteSeo,
  type AdminSeoEntry,
} from '../../api/admin';
import {
  AdminDetailCard,
  AdminEmptyState,
  AdminKpiStrip,
  AdminPageLayout,
  AdminSearchInput,
  AdminStatusBadge,
  AdminTablePanel,
} from '../../components/admin/AdminPageLayout';
import { LoadingState, ErrorState } from '../../components/states/StateViews';

function entityIcon(type: string) {
  return type === 'blog' ? Newspaper : FileText;
}

function completenessBadge(entry: AdminSeoEntry) {
  if (entry.hasSeoTitle && entry.hasSeoDescription) return 'complete';
  if (!entry.hasSeoTitle && !entry.hasSeoDescription) return 'missing';
  return 'partial';
}

function SeoDetailCard({ entryKey, onClose }: { entryKey: { entityType: string; id: string } | null; onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-seo-entry', entryKey?.entityType, entryKey?.id],
    queryFn: () => fetchAdminSeoEntry(entryKey!.entityType, entryKey!.id),
    enabled: Boolean(entryKey),
  });
  const update = useMutation({
    mutationFn: (body: { seoTitle?: string | null; seoDescription?: string | null }) =>
      patchAdminSeoEntry(entryKey!.entityType, entryKey!.id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-seo'] });
      void qc.invalidateQueries({ queryKey: ['admin-seo-entry', entryKey?.entityType, entryKey?.id] });
    },
  });
  const row = data?.entry;

  return (
    <AdminDetailCard open={Boolean(entryKey)} onClose={onClose} ariaLabel="SEO entry" breadcrumb="SEO entry" title={row?.title ?? 'Loading…'} illustration="admin.module.seo"
      subtitle={row ? (<><span className="chip-pill">{row.entityType}</span><AdminStatusBadge status={completenessBadge(row)} /><span className="wfx-muted wfx-admin-subline">/{row.slug}</span></>) : undefined}>
      {isLoading && <LoadingState label="Loading SEO entry…" />}
      {isError && <ErrorState message="Could not load SEO entry." onRetry={() => void refetch()} />}
      {row && (
        <div className="wfx-admin-form">
          <label className="wfx-admin-field"><span>SEO title</span><input defaultValue={row.seoTitle ?? ''} placeholder={row.title} onBlur={(e) => { const v = e.target.value.trim(); if (v !== (row.seoTitle ?? '')) update.mutate({ seoTitle: v || null }); }} /></label>
          <label className="wfx-admin-field"><span>SEO description</span><textarea rows={4} className="wfx-admin-textarea" defaultValue={row.seoDescription ?? ''} onBlur={(e) => { const v = e.target.value.trim(); if (v !== (row.seoDescription ?? '')) update.mutate({ seoDescription: v || null }); }} /></label>
        </div>
      )}
    </AdminDetailCard>
  );
}

function SeoCard({ row, onSelect }: { row: AdminSeoEntry; onSelect: () => void }) {
  const Icon = entityIcon(row.entityType);
  return (
    <button type="button" className="wfx-admin-seo-card glass-surface" onClick={onSelect}>
      <div className="wfx-admin-media-card-icon"><Icon size={24} aria-hidden="true" /></div>
      <div className="wfx-admin-page-card-copy"><strong>{row.title}</strong><span className="wfx-muted">{row.entityType} · /{row.slug}</span></div>
      <div className="wfx-admin-page-card-stats"><AdminStatusBadge status={completenessBadge(row)} />{row.seoTitle && <span className="wfx-muted">{row.seoTitle.slice(0, 40)}…</span>}</div>
    </button>
  );
}

export function AdminSeoScreen() {
  const qc = useQueryClient();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');
  const [completenessFilter, setCompletenessFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<{ entityType: string; id: string } | null>(null);

  useEffect(() => { const t = window.setTimeout(() => setDebouncedQuery(query.trim()), 300); return () => window.clearTimeout(t); }, [query]);
  useEffect(() => { setPage(1); }, [debouncedQuery, entityFilter, completenessFilter]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-seo', debouncedQuery, entityFilter, completenessFilter, page],
    queryFn: () => fetchAdminSeo({ q: debouncedQuery || undefined, entityType: entityFilter, completeness: completenessFilter, page, limit: 12 }),
  });

  const updateSite = useMutation({
    mutationFn: patchAdminSiteSeo,
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['admin-seo'] }),
  });

  const rows = data?.data ?? [];
  const site = data?.site;
  const stats = data?.stats;
  const pagination = data?.pagination;
  const kpiItems = useMemo(() => [
    { label: 'Total entries', value: stats?.total ?? 0 },
    { label: 'Complete SEO', value: stats?.complete ?? 0 },
    { label: 'Missing title', value: stats?.missingTitle ?? 0 },
    { label: 'Missing description', value: stats?.missingDescription ?? 0 },
  ], [stats]);

  if (isLoading && !data) return <LoadingState label="Loading SEO data…" />;
  if (isError) return <ErrorState message="Could not load SEO manager." onRetry={() => void refetch()} />;

  return (
    <>
      <AdminPageLayout breadcrumb="Admin / SEO Manager" title="SEO Manager" description="Optimize site metadata and page-level SEO for opportunities and blog posts." icon={Search} illustration="admin.module.seo">
        <AdminKpiStrip items={kpiItems} />
        <article className="wfx-admin-panel glass-surface wfx-admin-seo-site-panel">
          <h2>Site metadata</h2>
          <div className="wfx-admin-form">
            <label className="wfx-admin-field"><span>Platform name</span><input readOnly value={site?.name ?? ''} /></label>
            <label className="wfx-admin-field"><span>Tagline</span><input defaultValue={site?.tagline ?? ''} onBlur={(e) => { const v = e.target.value.trim(); if (v !== (site?.tagline ?? '')) updateSite.mutate({ tagline: v || null }); }} /></label>
            <label className="wfx-admin-field"><span>SEO title</span><input defaultValue={site?.seoTitle ?? ''} placeholder={site?.name ?? 'WebFoxx Results'} onBlur={(e) => { const v = e.target.value.trim(); if (v !== (site?.seoTitle ?? '')) updateSite.mutate({ seoTitle: v || null }); }} /></label>
            <label className="wfx-admin-field"><span>SEO description</span><textarea rows={3} className="wfx-admin-textarea" defaultValue={site?.seoDescription ?? ''} onBlur={(e) => { const v = e.target.value.trim(); if (v !== (site?.seoDescription ?? '')) updateSite.mutate({ seoDescription: v || null }); }} /></label>
            <label className="wfx-admin-field"><span>Meta keywords</span><input defaultValue={site?.metaKeywords ?? ''} placeholder="results, government jobs, vault" onBlur={(e) => { const v = e.target.value.trim(); if (v !== (site?.metaKeywords ?? '')) updateSite.mutate({ metaKeywords: v || null }); }} /></label>
          </div>
        </article>
        <div className="wfx-admin-blog-grid">{rows.map((row) => <SeoCard key={`${row.entityType}-${row.id}`} row={row} onSelect={() => setSelected({ entityType: row.entityType, id: row.id })} />)}</div>
        {rows.length === 0 && <AdminEmptyState message="No SEO entries match your filters." />}
        <AdminTablePanel title="Page SEO directory" toolbar={(
          <div className="wfx-admin-toolbar">
            <AdminSearchInput value={query} onChange={setQuery} placeholder="Search title, slug, or SEO text…" />
            <select className="wfx-admin-select" value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} aria-label="Filter by entity type">
              <option value="all">All types</option><option value="opportunity">Opportunities</option><option value="blog">Blog posts</option>
            </select>
            <select className="wfx-admin-select" value={completenessFilter} onChange={(e) => setCompletenessFilter(e.target.value)} aria-label="Filter by completeness">
              <option value="all">All entries</option><option value="complete">Complete</option><option value="missing_title">Missing title</option><option value="missing_description">Missing description</option>
            </select>
          </div>
        )} footer={pagination && pagination.pages > 1 ? (
          <div className="wfx-admin-pagination">
            <button type="button" className="secondary-action compact" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}><ChevronLeft size={16} /> Previous</button>
            <span className="wfx-muted">Page {pagination.page} of {pagination.pages} · {pagination.total} entries</span>
            <button type="button" className="secondary-action compact" disabled={page >= pagination.pages} onClick={() => setPage((p) => p + 1)}>Next <ChevronRight size={16} /></button>
          </div>
        ) : undefined}>
          <table className="wfx-admin-table"><thead><tr><th>Title</th><th>Type</th><th>Slug</th><th>SEO title</th><th>Status</th></tr></thead>
            <tbody>{rows.map((row) => (
              <tr key={`${row.entityType}-${row.id}`} className="wfx-admin-table-row-clickable" onClick={() => setSelected({ entityType: row.entityType, id: row.id })}>
                <td><strong>{row.title}</strong></td><td>{row.entityType}</td><td>{row.slug}</td><td>{row.seoTitle ?? '—'}</td><td><AdminStatusBadge status={completenessBadge(row)} /></td>
              </tr>
            ))}</tbody>
          </table>
        </AdminTablePanel>
      </AdminPageLayout>
      <SeoDetailCard entryKey={selected} onClose={() => setSelected(null)} />
    </>
  );
}
